import { useEffect, useState, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { buildApiUrl, getApiBaseUrl } from '../../config/apiConfig';
import { useCart } from '../../context/CartContext';
import { useAuth } from '../../context/AuthContext';
import { useNotifications } from '../../context/NotificationContext';
import './CoursesDetallePage.css';
import { logError, warn } from '../../utils/logger';
import { formatARS } from '../../utils/currency';

const resolveImageUrl = (source) => {
  if (!source) return null;
  const sanitized = String(source).replace(/\\/g, '/').replace(/\s/g, '%20');
  if (/^https?:\/\//i.test(sanitized)) return sanitized;
  const base = getApiBaseUrl();
  if (sanitized.startsWith('/')) return `${base}${sanitized}`;
  if (sanitized.startsWith('uploads/')) return `${base}/${sanitized}`;
  return `${base}/uploads/${sanitized}`;
};

const CoursesDetallePage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { addToCart } = useCart();
  const { user, token } = useAuth();
  const { notify } = useNotifications();
  const [course, setCourse] = useState(null);
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedModules, setExpandedModules] = useState({});
  const [acquiring, setAcquiring] = useState(false);
  const [stats, setStats] = useState({ classes: 0, modules: 0, hours: 0 });

  useEffect(() => {
    fetchCourseData();
  }, [id, user, token]);

  const fetchCourseData = async () => {
    try {
      setLoading(true);
      
      // Obtener el curso con módulos y clases
      const courseResponse = await fetch(buildApiUrl(`/api/courses/public/${id}`));
      if (!courseResponse.ok) {
        throw new Error('Curso no encontrado');
      }
      const courseData = await courseResponse.json();
      setCourse(courseData.course);

      // Calcular estadísticas
      const calculatedStats = calculateStats(courseData.course);
      setStats(calculatedStats);

      // Obtener el producto asociado para el precio
      try {
        const productResponse = await fetch(buildApiUrl(`/api/products/public/course/${id}/complete`));
        if (productResponse.ok) {
          const productData = await productResponse.json();
          setProduct(productData.product || productData);
          if (productData.stats) {
            setStats(productData.stats);
          }
        } else {
          // Fallback a ruta simple
          const simpleResponse = await fetch(buildApiUrl(`/api/products/public/course/${id}`));
          if (simpleResponse.ok) {
            const simpleData = await simpleResponse.json();
            setProduct(simpleData.product || simpleData);
          }
        }
      } catch (err) {
        warn('No se pudo obtener el producto del curso:', err);
      }
    } catch (err) {
      logError('[CoursesDetallePage] Error fetching course:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const calculateStats = (courseData) => {
    if (!courseData?.modules) return { classes: 0, modules: 0, hours: 0 };
    
    const totalModules = courseData.modules.length;
    let totalClasses = 0;
    let totalMinutes = 0;

    courseData.modules.forEach((module) => {
      if (module.clases) {
        totalClasses += module.clases.length;
        module.clases.forEach((clase) => {
          totalMinutes += clase.minutes || 0;
        });
      }
    });

    const totalHours = Math.round((totalMinutes / 60) * 10) / 10;

    return {
      classes: totalClasses,
      modules: totalModules,
      hours: totalHours,
    };
  };

  const calculateFinalPrice = (price, discountPercent) => {
    const numPrice = parseFloat(price || 0);
    const numDiscount = parseFloat(discountPercent || 0);
    if (!numDiscount || numDiscount === 0) return numPrice;
    return numPrice * (1 - numDiscount / 100);
  };

  const handleAddToCart = async () => {
    if (!product) {
      warn('No hay producto disponible para agregar al carrito');
      return;
    }
    
    const courseImage = course?.imagen || course?.image || null;
    const imageUrl = courseImage ? resolveImageUrl(courseImage) : null;
    
    const courseId = product.referenceId || course?.id;
    
    // Calcular precio final con descuento
    const coursePrice = parseFloat(product?.price || 0);
    const discountPercent = parseFloat(product?.discountPercent || course?.discountPercent || 0);
    const finalPrice = calculateFinalPrice(coursePrice, discountPercent);
    
    const success = await addToCart(
      {
        id: courseId,
        name: course?.nombre || course?.titulo || 'Curso sin nombre',
        price: finalPrice,
        image: imageUrl || '',
        productType: 'course',
        quantity: 1,
      },
      token,
      (errorMessage) => {
        notify({
          message: errorMessage,
          type: 'warning',
        });
      }
    );
    
    if (success) {
      notify({
        message: 'El curso se agregó al carrito exitosamente.',
        type: 'success',
      });
    }
  };

  const handleBuyNow = async () => {
    await handleAddToCart();
    setTimeout(() => {
      navigate('/cart');
    }, 500);
  };

  const handleAcquireFree = async () => {
    if (!user || !token) {
      navigate('/login', { state: { from: `/courses/${id}` } });
      return;
    }

    if (!product || !product.id) {
      notify({
        message: 'No se pudo obtener la información del producto.',
        type: 'error',
      });
      return;
    }

    setAcquiring(true);
    try {
      const response = await fetch(buildApiUrl('/api/products/grant-access'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          productId: product.id,
          productType: 'course',
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Error desconocido' }));
        throw new Error(errorData.error || `Error al adquirir el curso (${response.status})`);
      }

      notify({
        message: '¡Curso adquirido! El curso gratuito se agregó a tu cuenta exitosamente.',
        type: 'success',
      });

      setTimeout(() => {
        navigate('/my-courses');
      }, 1500);
    } catch (err) {
      logError('[CoursesDetallePage] Error adquiriendo curso gratuito:', err);
      notify({
        message: err.message || 'No se pudo adquirir el curso. Por favor, intenta nuevamente.',
        type: 'error',
      });
    } finally {
      setAcquiring(false);
    }
  };

  const toggleModule = (moduleId) => {
    setExpandedModules((prev) => ({
      ...prev,
      [moduleId]: !prev[moduleId],
    }));
  };

  if (loading) {
    return (
      <div className="course-detail-loading">
        <div className="course-detail-spinner"></div>
        <p>Cargando curso...</p>
      </div>
    );
  }

  if (error || !course) {
    return (
      <div className="course-detail-error">
        <h2>Curso no encontrado</h2>
        <p>{error || 'El curso que buscas no existe o no está disponible.'}</p>
        <button onClick={() => navigate('/courses')} className="course-detail-back-btn">
          Volver a cursos
        </button>
      </div>
    );
  }

  const coursePrice = parseFloat(product?.price || 0);
  const discountPercent = parseFloat(product?.discountPercent || course?.discountPercent || 0);
  const finalPrice = calculateFinalPrice(coursePrice, discountPercent);
  const hasDiscount = discountPercent > 0;
  const isFree = finalPrice === 0 || coursePrice === 0;
  const courseImage = resolveImageUrl(course.imagen);
  const isAcquired = product?.isAcquired || false;
  const tags = course.tags || course.etiquetas || product?.tags || ['Educación'];
  const instructor = course.instructor || course.instructorName || product?.instructor || 'Instructor Certificado';

  return (
    <div className="course-detail-page">
      {/* Breadcrumb */}
      <div className="course-detail-breadcrumb">
        <button onClick={() => navigate('/courses')} className="course-detail-breadcrumb-link">
          <i className="fas fa-arrow-left"></i>
          Volver a Cursos
        </button>
      </div>

      {/* Hero Section */}
      <section className="course-detail-hero">
        <div className="course-detail-hero-container">
          <div className="course-detail-hero-content">
            <div className="course-detail-image-wrapper">
              {courseImage ? (
                <img 
                  src={courseImage} 
                  alt={course.nombre || 'Imagen del curso'} 
                  className="course-detail-image"
                />
              ) : (
                <div className="course-detail-image-placeholder">
                  <i className="fas fa-graduation-cap"></i>
                </div>
              )}
              <div className="course-detail-badge">Curso</div>
              {hasDiscount && (
                <div className="course-detail-discount-badge-hero">
                  <i className="fas fa-tag"></i>
                  <span>-{discountPercent}% OFF</span>
                </div>
              )}
            </div>
            
            <div className="course-detail-info">
              <h1 className="course-detail-title">{course.nombre || 'Curso sin nombre'}</h1>
              
              {course.descripcionBreve && (
                <p className="course-detail-subtitle">{course.descripcionBreve}</p>
              )}

              <div className="course-detail-stats">
                <div className="course-detail-stat">
                  <i className="fas fa-play-circle"></i>
                  <div>
                    <span className="course-detail-stat-number">{stats.classes}</span>
                    <span className="course-detail-stat-label">Clases</span>
                  </div>
                </div>
                <div className="course-detail-stat">
                  <i className="fas fa-layer-group"></i>
                  <div>
                    <span className="course-detail-stat-number">{stats.modules}</span>
                    <span className="course-detail-stat-label">Módulos</span>
                  </div>
                </div>
                <div className="course-detail-stat">
                  <i className="fas fa-clock"></i>
                  <div>
                    <span className="course-detail-stat-number">{stats.hours}h</span>
                    <span className="course-detail-stat-label">Duración</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <div className="course-detail-content">
        <div className="course-detail-main">
          {/* Info Section */}
          <div className="course-detail-info-section">
            <div className="course-detail-info-header">
              <h2 className="course-detail-info-title">Información del Curso</h2>
              <div className="course-detail-tags">
                {tags.map((tag, index) => (
                  <span key={index} className="course-detail-tag">{tag}</span>
                ))}
              </div>
            </div>
            
            <div className="course-detail-info-content">
              {/* Description */}
              {course.descripcionExtendida && (
                <div className="course-detail-description-section">
                  <h3 className="course-detail-section-title">Descripción</h3>
                  <div 
                    className="course-detail-description-content"
                    dangerouslySetInnerHTML={{ __html: course.descripcionExtendida }}
                  />
                </div>
              )}

              {/* Features */}
              <div className="course-detail-features-section">
                <h3 className="course-detail-section-title">Características</h3>
                <div className="course-detail-features-grid">
                  <div className="course-detail-feature">
                    <i className="fas fa-infinity"></i>
                    <span>Acceso ilimitado</span>
                  </div>
                  <div className="course-detail-feature">
                    <i className="fas fa-certificate"></i>
                    <span>Certificado incluido</span>
                  </div>
                  <div className="course-detail-feature">
                    <i className="fas fa-headset"></i>
                    <span>Soporte 24/7</span>
                  </div>
                  <div className="course-detail-feature">
                    <i className="fas fa-mobile-alt"></i>
                    <span>Acceso móvil</span>
                  </div>
                </div>
              </div>

              {/* Curriculum */}
              {course.modules && course.modules.length > 0 && (
                <div className="course-detail-curriculum-section">
                  <h3 className="course-detail-section-title">Currículum del Curso</h3>
                  <div className="course-detail-curriculum">
                    {course.modules.map((module, moduleIndex) => {
                      const moduleId = module.id || moduleIndex;
                      const isExpanded = expandedModules[moduleId] || false;
                      const classes = module.clases || [];
                      const moduleMinutes = classes.reduce((total, clase) => total + (clase.minutes || 0), 0);
                      const moduleHours = Math.round((moduleMinutes / 60) * 10) / 10;
                      
                      return (
                        <div key={moduleId} className="course-detail-module">
                          <div 
                            className="course-detail-module-header"
                            onClick={() => toggleModule(moduleId)}
                          >
                            <div className="course-detail-module-title">
                              <h4>Módulo {moduleIndex + 1}: {module.nombre || module.title || `Módulo ${moduleIndex + 1}`}</h4>
                              <span className="course-detail-module-stats">
                                {classes.length} clases • {moduleHours}h
                              </span>
                            </div>
                            <i className={`fas fa-chevron-${isExpanded ? 'up' : 'down'} course-detail-module-arrow`}></i>
                          </div>
                          
                          {isExpanded && (
                            <div className="course-detail-module-content">
                              {module.descripcionBreve && (
                                <p className="course-detail-module-description">
                                  {module.descripcionBreve}
                                </p>
                              )}
                              
                              {classes.length > 0 && (
                                <div className="course-detail-classes-list">
                                  {classes.map((clase, classIndex) => (
                                    <div key={clase.id || classIndex} className="course-detail-class">
                                      <div className="course-detail-class-icon">
                                        <i className="fas fa-play-circle"></i>
                                      </div>
                                      <div className="course-detail-class-content">
                                        <h5>{clase.nombre || clase.title || `Clase ${classIndex + 1}`}</h5>
                                        {clase.descripcionBreve && (
                                          <p>{clase.descripcionBreve}</p>
                                        )}
                                      </div>
                                      <div className="course-detail-class-duration">
                                        {clase.minutes ? `${Math.round(clase.minutes / 60 * 10) / 10}h` : '-'}
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Sidebar */}
        <div className="course-detail-sidebar">
          <div className="course-detail-purchase-card">
            <div className="course-detail-price-section">
              {hasDiscount && (
                <div className="course-detail-price-original">
                  {formatARS(coursePrice)}
                </div>
              )}
              <div className="course-detail-price-current">
                {isFree ? 'Gratis' : formatARS(finalPrice)}
              </div>
              {hasDiscount && (
                <div className="course-detail-price-discount">
                  -{discountPercent}%
                </div>
              )}
            </div>
            
            <div className="course-detail-purchase-actions">
              {isAcquired ? (
                <button 
                  className="course-detail-go-to-courses-btn"
                  onClick={() => navigate('/my-courses')}
                >
                  <i className="fas fa-graduation-cap"></i>
                  Ir a mis cursos
                </button>
              ) : isFree ? (
                <button 
                  className="course-detail-buy-btn"
                  onClick={handleAcquireFree}
                  disabled={acquiring || !user}
                >
                  {acquiring ? (
                    <>
                      <span className="course-detail-spinner-small"></span>
                      Adquiriendo...
                    </>
                  ) : (
                    <>
                      <i className="fas fa-gift"></i>
                      Adquirir Gratis
                    </>
                  )}
                </button>
              ) : (
                <>
                  <button 
                    className="course-detail-purchase-btn"
                    onClick={handleAddToCart}
                  >
                    <i className="fas fa-shopping-cart"></i>
                    Agregar al Carrito
                  </button>
                  <button 
                    className="course-detail-buy-btn"
                    onClick={handleBuyNow}
                  >
                    <i className="fas fa-credit-card"></i>
                    Comprar Ahora
                  </button>
                </>
              )}
            </div>
            
            <div className="course-detail-guarantee">
              <i className="fas fa-shield-alt"></i>
              <span>Garantía de satisfacción</span>
            </div>
          </div>
          
          <div className="course-detail-instructor-card">
            <h4 className="course-detail-instructor-title">Instructor</h4>
            <div className="course-detail-instructor-info">
              <div className="course-detail-instructor-avatar">
                <i className="fas fa-user"></i>
              </div>
              <div className="course-detail-instructor-details">
                <span className="course-detail-instructor-name">{instructor}</span>
                <span className="course-detail-instructor-title-text">Instructor Certificado</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CoursesDetallePage;
